import React, { useEffect, useState, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import apiClient from '../api/client';
import { toast } from "sonner";
import { Loader2, Save, ArrowLeft, LogOut, RotateCcw, Check } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { CATEGORIES } from '../lib/categoryConfig';

/** Standardvärden för snabbuppskattning Q2-multiplikatorer (`qe_{slug}_mult_{key}`). */
const QE_TYPE_MULT_DEFAULTS = Object.fromEntries(
  CATEGORIES.flatMap((c) =>
    (c.qe?.q2Options ?? []).map((o) => [`qe_${c.slug}_mult_${o.key}`, o.multiplier])
  )
);

// Keep in sync with backend `AppSettings` defaults.
const DEFAULT_SETTINGS = {
  // ── Global ──
  hourly_rate_privat: 875.0,
  hourly_rate_company: 550.0,
  overall_multiplier: 1.2,
  min_hours_median: 16.0,
  ROT_RATE: 0.30,

  // Kok - bas-timmar
  h_etablering: 1.5,
  h_inmatning: 1.2,
  h_planering: 0.8,
  h_slutfinish: 1.5,
  h_kontroll: 0.8,
  h_stad: 1.3,
  h_per_base_cab: 0.8,
  h_per_wall_cab: 1.0,
  h_per_tall_cab: 1.3,
  h_per_front: 0.18,
  h_per_side: 0.5,
  h_per_filler: 0.7,
  h_per_sockel_m: 0.09,
  h_worktop_laminate: 2.5,
  h_worktop_stone: 2.0,
  h_worktop_cutout: 0.8,
  h_app_dishwasher: 1.0,
  h_app_oven: 0.6,
  h_app_fridge: 1.2,
  h_app_hob: 0.4,
  h_app_fan: 0.8,

  logistics_normal: 1.08,
  logistics_hard: 1.18,

  fee_demolition_small: 6000.0,
  fee_demolition_std: 7500.0,
  fee_demolition_large: 9000.0,
  fee_disposal_run: 5000.0,
  fee_electric_base: 5600.0,
  fee_electric_med: 11400.0,
  fee_electric_plus: 17100.0,
  fee_plumbing_base: 4500.0,
  fee_plumbing_move: 10200.0,
  fee_plumbing_plus: 14800.0,
  fee_procurement: 2500.0,

  paint_wall_h_m2: 0.55,
  paint_ceil_h_m2: 0.45,
  paint_mult: 1.15,

  backsplash_h_m: 4.0,
  backsplash_add_gips: 4.0,
  backsplash_add_concrete: 1.0,
  backsplash_add_unknown: 2.5,

  span_90_complete: 0.07,
  span_80_complete: 0.10,
  span_70_complete: 0.15,
  span_60_complete: 0.20,
  span_low_complete: 0.25,

  // ── QE per kategori ──
  qe_malning_fee_small: 8900, qe_malning_fee_standard: 18900, qe_malning_fee_large: 34900, qe_malning_span: 0.25, qe_malning_rot_labor_share: 0.80,
  qe_kok_fee_small: 29900, qe_kok_fee_standard: 39900, qe_kok_fee_large: 54900, qe_kok_span: 0.25, qe_kok_rot_labor_share: 0.85,
  qe_vatrum_fee_small: 69000, qe_vatrum_fee_standard: 119000, qe_vatrum_fee_large: 179000, qe_vatrum_span: 0.30, qe_vatrum_rot_labor_share: 0.55,
  qe_kakel_fee_small: 12900, qe_kakel_fee_standard: 24900, qe_kakel_fee_large: 44900, qe_kakel_span: 0.25, qe_kakel_rot_labor_share: 0.65,
  qe_golv_fee_small: 9900, qe_golv_fee_standard: 19900, qe_golv_fee_large: 34900, qe_golv_span: 0.25, qe_golv_rot_labor_share: 0.70,
  qe_vvs_fee_small: 3900, qe_vvs_fee_standard: 7900, qe_vvs_fee_large: 14900, qe_vvs_span: 0.25, qe_vvs_rot_labor_share: 0.60,
  qe_el_fee_small: 2900, qe_el_fee_standard: 6900, qe_el_fee_large: 12900, qe_el_span: 0.25, qe_el_rot_labor_share: 0.60,
  qe_snickeri_fee_small: 7900, qe_snickeri_fee_standard: 17900, qe_snickeri_fee_large: 34900, qe_snickeri_span: 0.25, qe_snickeri_rot_labor_share: 0.75,

  // ── Full wizard params per kategori ──
  // Målning
  malning_min_hours_median: 8, malning_paint_wall_h_per_m2: 0.55, malning_paint_ceiling_h_per_m2: 0.45,
  malning_paint_trim_h_per_meter: 0.08, malning_wallpaper_h_per_m2: 0.22, malning_strip_wallpaper_h_per_m2: 0.18,
  // Kakel
  kakel_min_hours_median: 8, kakel_tile_wall_h_per_m2: 1.25, kakel_tile_floor_h_per_m2: 1.10,
  kakel_tile_demo_h_per_m2: 0.60, kakel_corner_h_each: 0.35, kakel_mosaic_multiplier: 1.35,
  kakel_large_tile_multiplier: 1.15, kakel_concrete_tile_multiplier: 1.15,
  // Golv
  golv_min_hours_median: 6, golv_install_wood_h_per_m2: 0.28, golv_install_laminate_h_per_m2: 0.24,
  golv_install_vinyl_h_per_m2: 0.22, golv_install_carpet_h_per_m2: 0.25, golv_remove_h_per_m2: 0.18,
  golv_sand_h_per_m2: 0.30, golv_finish_lacquer_h_per_m2: 0.08, golv_finish_oil_h_per_m2: 0.06,
  golv_baseboard_h_per_m: 0.10,
  // VVS
  vvs_fee_connect: 3900, vvs_fee_connect_medium: 6900, vvs_fee_connect_large: 9900,
  vvs_fee_replace_fixture: 3500, vvs_fee_drain_small: 2900, vvs_fee_drain_large: 5900,
  vvs_fee_move_small: 8900, vvs_fee_move_medium: 12900, vvs_fee_move_large: 18900,
  vvs_subcontractor_markup: 1.30,
  // El
  el_fee_basic: 2900, el_fee_outlet_extra: 1500, el_fee_lighting: 3900,
  el_fee_new_circuit: 6900, el_fee_troubleshoot: 2900,
  el_wall_open_multiplier: 1.20, el_long_run_multiplier: 1.15, el_concrete_multiplier: 1.15,
  el_subcontractor_markup: 1.30,
  // Snickeri
  snickeri_partition_h_per_m: 1.20, snickeri_ceiling_h_per_m2: 0.55, snickeri_trim_h_per_m: 0.10,
  snickeri_interior_door_h: 2.5, snickeri_exterior_door_h: 5.0, snickeri_casing_h_per_door: 1.5,
  snickeri_fee_remodel_small: 12900, snickeri_fee_remodel_medium: 24900, snickeri_fee_remodel_large: 39900,
  // Våtrum
  vatrum_wet_unknown_multiplier: 1.15, vatrum_wet_m2_fee: 8500,
  vatrum_base_fee_ytskikt: 45000, vatrum_base_fee_del: 85000, vatrum_base_fee_hel: 130000,

  ...QE_TYPE_MULT_DEFAULTS,
};

// Normalize decimal input (accepts both 1,5 and 1.5)
const normalizeDecimal = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const str = String(value).replace(',', '.').trim();
  const num = parseFloat(str);
  if (isNaN(num)) return 0;
  return num;
};

/** Maste ligga utanfor sidkomponenten - annars ny komponenttyp varje render och fokus/tangentbord forsvinner. */
function SettingInput({ label, field, unit = '', settings, onFieldChange }) {
  const value = settings?.[field];
  const displayValue = value !== null && value !== undefined ? String(value) : '';
  return (
    <div>
      <Label className="text-xs text-muted-foreground uppercase tracking-wider">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="text"
          inputMode="decimal"
          className="mt-1"
          value={displayValue}
          onChange={(e) => onFieldChange(field, e.target.value)}
          placeholder="0"
        />
        {unit ? <span className="text-sm text-muted-foreground">{unit}</span> : null}
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [lastSaved, setLastSaved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
        const res = await apiClient.get('/settings');
        const merged = { ...DEFAULT_SETTINGS, ...(res.data || {}) };
        setSettings(merged);
        setLastSaved(merged);
    } catch (e) {
        toast.error("Kunde inte hämta inställningar");
    } finally {
        setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
        // Normalize all known settings keys (DEFAULT_SETTINGS is the whitelist)
        const payload = {};
        for (const key of Object.keys(DEFAULT_SETTINGS)) {
          const raw = settings?.[key];
          const num = normalizeDecimal(raw);
          payload[key] = Number.isFinite(num) ? num : DEFAULT_SETTINGS[key];
        }

        await apiClient.post('/settings', payload, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token') || ''}` } });
        setLastSaved(payload);
        setSettings(payload);
        setJustSaved(true);
        toast.success("Inställningar sparade!");
        setTimeout(() => setJustSaved(false), 2000);
    } catch (e) {
        toast.error("Kunde inte spara: " + (e.response?.data?.detail || e.message));
    } finally {
        setSaving(false);
    }
  };

  const handleRestore = () => {
    if (lastSaved) {
      setSettings({...lastSaved});
      toast.info("Återställt till senast sparade");
    }
  };

  const handleInputChange = useCallback((field, val) => {
    setSettings((prev) => ({ ...prev, [field]: val }));
  }, []);

  const logout = () => {
      localStorage.removeItem('admin_token');
      navigate('/');
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
                <Button variant="outline" onClick={() => navigate('/admin/dashboard')}><ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka</Button>
                <h1 className="text-3xl font-bold text-primary">Inställningar</h1>
            </div>
            <div className="flex gap-2">
                <Button variant="outline" onClick={handleRestore} disabled={!lastSaved} title="Återställ senaste">
                    <RotateCcw className="h-4 w-4" />
                </Button>
                <Button onClick={handleSave} disabled={saving} className={`${justSaved ? 'bg-green-600' : 'bg-green-600'} hover:bg-green-700 text-white`}>
                    {saving ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : justSaved ? <Check className="mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
                    {justSaved ? 'Sparat!' : 'Spara'}
                </Button>
                <Button variant="outline" onClick={logout}><LogOut className="h-4 w-4" /></Button>
            </div>
        </div>

        <Tabs defaultValue="base">
            <TabsList className="flex flex-wrap gap-1 h-auto mb-2">
                <TabsTrigger value="base">Bas & Timmar</TabsTrigger>
                <TabsTrigger value="units">Enheter (Kök)</TabsTrigger>
                <TabsTrigger value="fees">Paket & Avgifter</TabsTrigger>
                <TabsTrigger value="paint">Målning/Stänk</TabsTrigger>
                <TabsTrigger value="config">Logistik & Risk</TabsTrigger>
                <TabsTrigger value="qe">QE-priser</TabsTrigger>
                <TabsTrigger value="cat_malning">Målning</TabsTrigger>
                <TabsTrigger value="cat_kakel">Kakel</TabsTrigger>
                <TabsTrigger value="cat_golv">Golv</TabsTrigger>
                <TabsTrigger value="cat_vvs">VVS</TabsTrigger>
                <TabsTrigger value="cat_el">El</TabsTrigger>
                <TabsTrigger value="cat_snickeri">Snickeri</TabsTrigger>
                <TabsTrigger value="cat_vatrum">Våtrum</TabsTrigger>
            </TabsList>

            <TabsContent value="base" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Globala Värden</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Timpris Privat (Inkl)" field="hourly_rate_privat" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Timpris Företag (Exkl)" field="hourly_rate_company" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Minimitid" field="min_hours_median" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Global Multiplikator" field="overall_multiplier" unit="x" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Bas-timmar (Engångs)</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Etablering" field="h_etablering" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Inmätning" field="h_inmatning" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Planering" field="h_planering" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Slutfinish" field="h_slutfinish" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Kontroll" field="h_kontroll" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Städning" field="h_stad" unit="h" />
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="units" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Skåp & Luckor (Timmar/st)</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-4 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Underskåp" field="h_per_base_cab" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Väggskåp" field="h_per_wall_cab" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Högskåp" field="h_per_tall_cab" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Lucka/Låda" field="h_per_front" unit="h" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Passbitar & Sockel</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Täcksida" field="h_per_side" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Passbit" field="h_per_filler" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Sockel (per m)" field="h_per_sockel_m" unit="h" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Bänkskiva & Vitvaror</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-4 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Laminat" field="h_worktop_laminate" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Sten (förarbete)" field="h_worktop_stone" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Urtag" field="h_worktop_cutout" unit="h" />
                        <div />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Diskmaskin" field="h_app_dishwasher" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Ugn/Micro" field="h_app_oven" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Kyl/Frys" field="h_app_fridge" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Häll" field="h_app_hob" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Fläkt" field="h_app_fan" unit="h" />
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="fees" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Rivning & Bortforsling (Fasta Priser)</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Rivning Liten (<8)" field="fee_demolition_small" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Rivning Std (8-14)" field="fee_demolition_std" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Rivning Stor (>14)" field="fee_demolition_large" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Bortforsling (per tur)" field="fee_disposal_run" unit="kr" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>El & VVS Paket (Fasta Priser)</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="El Bas" field="fee_electric_base" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="El Medium" field="fee_electric_med" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="El Plus" field="fee_electric_plus" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="VVS Bas" field="fee_plumbing_base" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="VVS Flytt" field="fee_plumbing_move" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="VVS Plus" field="fee_plumbing_plus" unit="kr" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Övrigt</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-2 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Inköpshjälp" field="fee_procurement" unit="kr" />
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="paint" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Målning</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Vägg (h/m2)" field="paint_wall_h_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Tak (h/m2)" field="paint_ceil_h_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Påslag" field="paint_mult" unit="x" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Stänkskydd</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-4 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Montering (h/m)" field="backsplash_h_m" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="+ Gips" field="backsplash_add_gips" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="+ Betong" field="backsplash_add_concrete" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="+ Vet ej" field="backsplash_add_unknown" unit="h" />
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="config" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Globalt</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-4">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="ROT-sats (0.30 = 30%)" field="ROT_RATE" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Logistikpåslag</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-2 gap-4">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Normal" field="logistics_normal" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Svår" field="logistics_hard" />
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle>Spann (Osäkerhet)</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-5 gap-4">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="90%" field="span_90_complete" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="80%" field="span_80_complete" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="70%" field="span_70_complete" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="60%" field="span_60_complete" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="<60%" field="span_low_complete" />
                    </CardContent>
                </Card>
            </TabsContent>

            {/* ── QE per kategori ── */}
            <TabsContent value="qe" className="space-y-4">
                <p className="text-sm text-muted-foreground">Snabbuppskattningspriser (inkl. moms) per kategori. Används på kategorisidorna.</p>
                {[
                  { slug: 'malning', label: '🖌️ Målning' },
                  { slug: 'kok', label: '🍳 Kök' },
                  { slug: 'vatrum', label: '🚿 Våtrum' },
                  { slug: 'kakel', label: '⬜ Kakel' },
                  { slug: 'golv', label: '🪵 Golv' },
                  { slug: 'vvs', label: '🔧 VVS' },
                  { slug: 'el', label: '⚡ El' },
                  { slug: 'snickeri', label: '🪚 Snickeri' },
                ].map(({ slug, label }) => (
                  <Card key={slug}>
                    <CardHeader><CardTitle className="text-base">{label}</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 sm:grid-cols-5 gap-4">
                      <SettingInput settings={settings} onFieldChange={handleInputChange} label="Litet (kr)" field={`qe_${slug}_fee_small`} unit="kr" />
                      <SettingInput settings={settings} onFieldChange={handleInputChange} label="Standard (kr)" field={`qe_${slug}_fee_standard`} unit="kr" />
                      <SettingInput settings={settings} onFieldChange={handleInputChange} label="Stort (kr)" field={`qe_${slug}_fee_large`} unit="kr" />
                      <SettingInput settings={settings} onFieldChange={handleInputChange} label="Spann ±" field={`qe_${slug}_span`} />
                      <SettingInput settings={settings} onFieldChange={handleInputChange} label="ROT-andel" field={`qe_${slug}_rot_labor_share`} />
                    </CardContent>
                  </Card>
                ))}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Typmultiplikatorer (fråga 2)</CardTitle>
                    <p className="text-sm text-muted-foreground font-normal">
                      Påverkar snabbuppskattningen per val i steg 2 (t.ex. väggar vs tak).
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {CATEGORIES.map((cat) => (
                      <div key={cat.slug}>
                        <p className="text-sm font-semibold text-primary mb-2">{cat.shortLabel || cat.label}</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                          {(cat.qe?.q2Options ?? []).map((opt) => (
                            <SettingInput
                              key={opt.key}
                              settings={settings}
                              onFieldChange={handleInputChange}
                              label={`${opt.label} (mult)`}
                              field={`qe_${cat.slug}_mult_${opt.key}`}
                              unit="x"
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
            </TabsContent>

            {/* Malning - fullwizard params */}
            <TabsContent value="cat_malning" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Malning: berakningsparametrar</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Minimitid" field="malning_min_hours_median" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Vägg (h/m²)" field="malning_paint_wall_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Tak (h/m²)" field="malning_paint_ceiling_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Lister (h/m)" field="malning_paint_trim_h_per_meter" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Tapetsering (h/m²)" field="malning_wallpaper_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Tapetrivning (h/m²)" field="malning_strip_wallpaper_h_per_m2" unit="h" />
                    </CardContent>
                </Card>
            </TabsContent>

            {/* Kakel */}
            <TabsContent value="cat_kakel" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Kakel: berakningsparametrar</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Minimitid" field="kakel_min_hours_median" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Vägg (h/m²)" field="kakel_tile_wall_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Golv (h/m²)" field="kakel_tile_floor_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Rivning (h/m²)" field="kakel_tile_demo_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Hörn (h/st)" field="kakel_corner_h_each" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Mosaik-mult" field="kakel_mosaic_multiplier" unit="x" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Stort format-mult" field="kakel_large_tile_multiplier" unit="x" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Betong-mult" field="kakel_concrete_tile_multiplier" unit="x" />
                    </CardContent>
                </Card>
            </TabsContent>

            {/* Golv */}
            <TabsContent value="cat_golv" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Golv: berakningsparametrar</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Minimitid" field="golv_min_hours_median" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Trä (h/m²)" field="golv_install_wood_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Laminat (h/m²)" field="golv_install_laminate_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Vinyl (h/m²)" field="golv_install_vinyl_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Matta (h/m²)" field="golv_install_carpet_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Rivning (h/m²)" field="golv_remove_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Slipning (h/m²)" field="golv_sand_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Lackering (h/m²)" field="golv_finish_lacquer_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Olja (h/m²)" field="golv_finish_oil_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Sockel (h/m)" field="golv_baseboard_h_per_m" unit="h" />
                    </CardContent>
                </Card>
            </TabsContent>

            {/* VVS */}
            <TabsContent value="cat_vvs" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>VVS: prispaket</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Inkoppling Liten" field="vvs_fee_connect" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Inkoppling Medium" field="vvs_fee_connect_medium" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Inkoppling Stor" field="vvs_fee_connect_large" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Byte armatur" field="vvs_fee_replace_fixture" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Stopp Liten" field="vvs_fee_drain_small" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Stopp Stor" field="vvs_fee_drain_large" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Flytt Liten" field="vvs_fee_move_small" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Flytt Medium" field="vvs_fee_move_medium" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Flytt Stor" field="vvs_fee_move_large" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="UE-påslag" field="vvs_subcontractor_markup" unit="x" />
                    </CardContent>
                </Card>
            </TabsContent>

            {/* El */}
            <TabsContent value="cat_el" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>El: prispaket</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Bas" field="el_fee_basic" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Extra uttag" field="el_fee_outlet_extra" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Belysning" field="el_fee_lighting" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Ny grupp" field="el_fee_new_circuit" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Felsökning" field="el_fee_troubleshoot" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Väggöppning-mult" field="el_wall_open_multiplier" unit="x" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Lång dragning-mult" field="el_long_run_multiplier" unit="x" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Betong-mult" field="el_concrete_multiplier" unit="x" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="UE-påslag" field="el_subcontractor_markup" unit="x" />
                    </CardContent>
                </Card>
            </TabsContent>

            {/* Snickeri */}
            <TabsContent value="cat_snickeri" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Snickeri: berakningsparametrar</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Innervägg (h/m)" field="snickeri_partition_h_per_m" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Undertak (h/m²)" field="snickeri_ceiling_h_per_m2" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Lister (h/m)" field="snickeri_trim_h_per_m" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Innerdörr (h)" field="snickeri_interior_door_h" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Ytterdörr (h)" field="snickeri_exterior_door_h" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Dörrfoder (h/dörr)" field="snickeri_casing_h_per_door" unit="h" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Ombyggnad Liten" field="snickeri_fee_remodel_small" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Ombyggnad Medium" field="snickeri_fee_remodel_medium" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Ombyggnad Stor" field="snickeri_fee_remodel_large" unit="kr" />
                    </CardContent>
                </Card>
            </TabsContent>

            {/* Vatrum */}
            <TabsContent value="cat_vatrum" className="space-y-4">
                <Card>
                    <CardHeader><CardTitle>Vatrum: parametrar</CardTitle></CardHeader>
                    <CardContent className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Basavgift ytskikt" field="vatrum_base_fee_ytskikt" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Basavgift delrenovering" field="vatrum_base_fee_del" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Basavgift helrenovering" field="vatrum_base_fee_hel" unit="kr" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Okänt skick-mult" field="vatrum_wet_unknown_multiplier" unit="x" />
                        <SettingInput settings={settings} onFieldChange={handleInputChange} label="Extra m²-avgift (kr/m²)" field="vatrum_wet_m2_fee" unit="kr" />
                    </CardContent>
                </Card>
            </TabsContent>

        </Tabs>
      </div>
    </div>
  );
}
